import React, { CSSProperties, Fragment } from 'react';
import { StyleSheet, View } from 'react-native';

import { Checkbox, Td, Tr } from '../../core-ui';
import { INVOICE_ITEMS } from '../../core/constants/Billing';
import formatCurrency from '../../core/support/formatCurrency';
import formatDate from '../../core/support/formatDate';
import { Addon } from '../../types/shared/Addon';
import {
  LineItem as LineItemType,
  NonPaymentLineItem as NonPaymentLineItemType,
} from '../../types/shared/Vendor';
import { Text } from '../uiold';
import { checkboxCell } from './VendorInvoiceSummary';
export type IsSelected = (item: NonPaymentLineItemType) => boolean;
export type ToggleSelection = (item: NonPaymentLineItemType) => void;

type Props = {
  addons: Map<string, Addon> | null | undefined;
  allowSelection?: boolean;
  currency: string;
  isSelected: IsSelected;
  lineItem: NonPaymentLineItemType;
  marketName: string;
  toggleSelection: ToggleSelection;
};

export function LineItem(props: Props) {
  const {
    addons,
    allowSelection,
    currency,
    isSelected,
    lineItem,
    marketName,
    toggleSelection,
  } = props;

  return lineItem.type === 'addon' ? (
    <Fragment>
      <Tr>
        {allowSelection ? (
          <Td style={checkboxCell}>
            {renderCheckbox({ isSelected, lineItem, toggleSelection })}
          </Td>
        ) : null}
        <Td style={dateCellStyle}>{formatDate(lineItem.created_at)}</Td>
        <Td title={lineItem.dates?.map((date) => date.slice(0, 10)).join(', ')}>
          {t('Addon: "{addonName}" for market "{marketName}"', {
            addonName: getAddonName(lineItem, addons),
            marketName,
          })}
          {lineItem.dates?.length
            ? lineItem.dates.length === 1
              ? t(' ({numberOfLineItemDates} day at {price} per day)', {
                  numberOfLineItemDates: lineItem.dates.length,
                  price: formatCurrency(
                    (lineItem.amount * 100) / lineItem.dates.length,
                    currency,
                  ),
                })
              : t(' ({numberOfLineItemDates} days at {price} per day)', {
                  numberOfLineItemDates: lineItem.dates.length,
                  price: formatCurrency(
                    (lineItem.amount * 100) / lineItem.dates.length,
                    currency,
                  ),
                })
            : ''}
        </Td>
        <Td style={{ textAlign: 'end', whiteSpace: 'nowrap' }}>
          {formatCurrency(lineItem.amount * 100, currency)}
        </Td>
      </Tr>
      <Tr>
        {allowSelection ? <Td /> : null}
        <Td />
        <Td style={{ padding: 1 }}>
          <View style={styles.flexRow}>
            {lineItem.dates?.map((date, key) => (
              <Text style={styles.dateItem} key={key}>
                {formatDate(date, { fromUTC: true })}
              </Text>
            ))}
          </View>
        </Td>
        <Td />
      </Tr>
    </Fragment>
  ) : lineItem.type === 'flat_fee' ||
    lineItem.type === 'redemption' ||
    lineItem.type === 'pct_of_sales' ? (
    <Fragment>
      <Tr>
        {allowSelection ? (
          <Td style={checkboxCell}>
            {renderCheckbox({ isSelected, lineItem, toggleSelection })}
          </Td>
        ) : null}
        <Td style={dateCellStyle}>{formatDate(lineItem.created_at)}</Td>
        <Td title={lineItem.dates?.map((date) => date.slice(0, 10)).join(', ')}>
          {getSalesFeeType(lineItem) === 'Redemptions' ||
          getSalesFeeType(lineItem) === 'Flat fees'
            ? t('{salesFeeType}: {salesFeeName} for market "{marketName}"', {
                salesFeeType: getSalesFeeType(lineItem),
                salesFeeName:
                  getSalesFeeType(lineItem) === 'Redemptions'
                    ? lineItem.redemption?.name ?? 'undefined'
                    : lineItem.flatFee?.name ?? 'undefined',
                marketName,
              })
            : getSalesFeeType(lineItem) === 'Percentage of sales'
            ? t(
                '{salesFeeType}: {pct}%, category: {categoryName} for market "{marketName}"',
                {
                  salesFeeType: getSalesFeeType(lineItem),
                  pct: String(lineItem.pctOfSales?.percentage),
                  categoryName:
                    lineItem.pctOfSales?.categoryName ?? 'undefined',
                  marketName,
                },
              )
            : null}
        </Td>
        <Td style={{ textAlign: 'end', whiteSpace: 'nowrap' }}>
          {formatCurrency(lineItem.amount * 100, currency)}
        </Td>
      </Tr>
      <Tr>
        {allowSelection ? <Td /> : null}
        <Td />
        <Td style={{ padding: 1 }}>
          <View style={styles.flexRow}>
            {lineItem.dates?.map((date, key) => (
              <Text style={styles.dateItem} key={key}>
                {formatDate(date, { fromUTC: true })}
              </Text>
            ))}
          </View>
        </Td>
        <Td />
      </Tr>
    </Fragment>
  ) : // TODO (#1508): Use `type` here instead of `name`
  lineItem.name === INVOICE_ITEMS.market_charges ? (
    <Fragment>
      <Tr>
        {allowSelection ? (
          <Td style={checkboxCell}>
            {renderCheckbox({ isSelected, lineItem, toggleSelection })}
          </Td>
        ) : null}
        <Td style={dateCellStyle}>{formatDate(lineItem.created_at)}</Td>
        <Td title={lineItem.dates?.map((date) => date.slice(0, 10)).join(', ')}>
          {t('Market Charges: "{marketName}"', { marketName })}
          {lineItem.dates?.length
            ? lineItem.dates.length === 1
              ? t(' ({numberOfLineItemDates} day at {price} per day)', {
                  numberOfLineItemDates: lineItem.dates.length,
                  price: formatCurrency(
                    (lineItem.amount * 100) / lineItem.dates.length,
                    currency,
                  ),
                })
              : t(' ({numberOfLineItemDates} days at {price} per day)', {
                  numberOfLineItemDates: lineItem.dates.length,
                  price: formatCurrency(
                    (lineItem.amount * 100) / lineItem.dates.length,
                    currency,
                  ),
                })
            : ''}
        </Td>
        <Td style={{ textAlign: 'end', whiteSpace: 'nowrap' }}>
          {formatCurrency(lineItem.amount * 100, currency)}
        </Td>
      </Tr>
      <Tr>
        {allowSelection ? <Td /> : null}
        <Td />
        <Td style={{ padding: 1 }}>
          <View style={styles.flexRow}>
            {lineItem.dates?.map((date, key) => (
              <Text style={styles.dateItem} key={key}>
                {formatDate(date, { fromUTC: true })}
              </Text>
            ))}
          </View>
        </Td>
        <Td />
      </Tr>
    </Fragment>
  ) : (
    <Tr>
      {allowSelection ? (
        <Td style={checkboxCell}>
          {renderCheckbox({ isSelected, lineItem, toggleSelection })}
        </Td>
      ) : null}
      <Td style={dateCellStyle}>{formatDate(lineItem.created_at)}</Td>
      <Td>{lineItem.name}</Td>
      <Td style={{ textAlign: 'end', whiteSpace: 'nowrap' }}>
        {formatCurrency(lineItem.amount * 100, currency)}
      </Td>
    </Tr>
  );
}

export function renderCheckbox(input: {
  lineItem: NonPaymentLineItemType;
  isSelected: (lineItem: NonPaymentLineItemType) => boolean;
  toggleSelection: (lineItem: NonPaymentLineItemType) => void;
}) {
  const { lineItem, isSelected, toggleSelection } = input;
  return (
    <View style={styles.checkboxContainer}>
      <Checkbox
        isChecked={isSelected(lineItem)}
        onChange={() => {
          toggleSelection(lineItem);
        }}
      />
    </View>
  );
}

function getSalesFeeType(
  lineItem: LineItemType,
): 'Flat fees' | 'Redemptions' | 'Percentage of sales' | '' {
  if (lineItem.type === 'flat_fee') {
    return 'Flat fees' as const;
  } else if (lineItem.type === 'redemption') {
    return 'Redemptions' as const;
  } else if (lineItem.type === 'pct_of_sales') {
    return 'Percentage of sales' as const;
  } else {
    return '';
  }
}

function getAddonName(
  lineItem: NonPaymentLineItemType,
  addons: Map<string, Addon> | null | undefined,
): string {
  if (addons && lineItem.addonId) {
    let addon = addons.get(lineItem.addonId);
    if (addon) {
      return addon.name;
    }
  }
  // Hacky: Try to extrapolate then name from the line item description. This is
  // necessary because in the past we weren't attaching the addonId to the
  // lineItem. We should add a migration to fix this. However even after that
  // it's possible an addon gets removed after which the addonId would still not
  // be sufficient to get the addon name.
  // The format of the lineItem description is:
  // "{addonName} at {marketName}"
  let match = lineItem.name.match(/^(.*?) at (.*)$/);
  return (match && match[1]) ?? '';
}

export const dateCellStyle: CSSProperties = {
  whiteSpace: 'nowrap',
};

const styles = StyleSheet.create({
  checkboxContainer: {
    ...StyleSheet.absoluteFillObject,
    alignItems: 'center',
    justifyContent: 'center',
  },
  dateItem: {
    fontSize: 14,
    marginHorizontal: 10,
    marginVertical: 10,
    textAlign: 'center',
    width: 100,
  },
  flexRow: {
    flexDirection: 'row',
    flexWrap: 'wrap',
  },
});
